import React from 'react';
import { ProductCard } from '../components/ProductCard';
import { HeroImage } from '../components/HeroImage';
import { ArrowRight, Network, Zap, Users, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';

export const Home = () => {
  const products = [
  {
    name: 'LinkIQ',
    description: 'AI-powered link intelligence and SEO visibility tools.',
    url: 'https://linkiq.pro/',
    logo: 'https://customer-assets.emergentagent.com/job_optiflo-corporate/artifacts/h46cl5nj_linkiq-logo-light.png'
  },
  {
    name: 'Opti Flo Connect',
    description: 'Universal integration and control plane for modern apps and GoHighLevel.',
    url: 'https://connect.optiflo.app/',
    logo: 'https://customer-assets.emergentagent.com/job_optiflo-corporate/artifacts/x6xqhklg_Opti%20Flo%20Connect%20Logo%20P3d%20White.png'
  },
  {
    name: 'HighReach Apps',
    description: 'Smart Websites and Apps built for local and small businesses.',
    url: 'https://highreachapps.com',
    logo: '/highreach-logo.png'
  },
  {
    name: 'Voyae',
    description: 'Social-first travel discovery and booking through real experiences.',
    url: 'https://govoyae.com/',
    logo: 'https://customer-assets.emergentagent.com/job_optiflo-corporate/artifacts/bg1cco3y_Voyae%20Finale%20Logo%27s%20%281%29.png'
  }];


  const audiences = [
  {
    icon: <Network size={32} />,
    title: 'Agencies',
    description: 'Scale your operations with AI-powered automation and modern integrations.'
  },
  {
    icon: <Zap size={32} />,
    title: 'Founders',
    description: 'Build faster with modular platforms designed for rapid growth.'
  },
  {
    icon: <Users size={32} />,
    title: 'Creators',
    description: 'Transform your content into revenue with social-first experiences.'
  },
  {
    icon: <Globe size={32} />,
    title: 'Service Businesses',
    description: 'Modernize your frontend and streamline client interactions.'
  }];


  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <HeroImage
        title="AI-FIRST PLATFORMS FOR MODERN BUSINESSES"
        subtitle="A growing suite of tools spanning automation, integrations, marketing intelligence, and experiential commerce."
      >
        <Link to="/products" className="btn-primary inline-flex items-center gap-2">
          Explore Products
          <ArrowRight size={20} className="text-inherit" />
        </Link>
      </HeroImage>

      {/* Products Grid - White Background */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-5 md:px-10">
          <div className="text-center mb-16">
            <h2 className="heading-2 mb-4">OUR PRODUCT ECOSYSTEM</h2>
            <p className="body-large max-w-3xl mx-auto text-on-white text-muted-white">
              Independent products with a shared backbone, built for the modern web.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-6xl mx-auto">
            {products.map((product) =>
            <ProductCard key={product.name} {...product} />
            )}
          </div>
        </div>
      </section>

      {/* How Products Fit Together - Dark Background */}
      <section className="py-20 md:py-32 bg-black">
        <div className="container mx-auto px-5 md:px-10">
          <h2 className="heading-2 text-center mb-16">
            HOW OUR PRODUCTS FIT TOGETHER
          </h2>
          
          <div className="max-w-4xl mx-auto bg-black rounded-2xl border border-gray-800 p-8 md:p-12">
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="bg-gradient-to-br from-[#00d9ff] via-[#0066ff] to-[#f43cde] text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                  1
                </div>
                <div>
                  <h3 className="heading-5 mb-2">Foundation Layer</h3>
                  <p className="body-medium text-on-dark">
                    <strong>Opti Flo</strong> provides the AI-first frontend platform, while <strong>Opti Flo Connect</strong> handles universal integrations, automation, and data flow between systems.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-gradient-to-br from-[#00d9ff] via-[#0066ff] to-[#f43cde] text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                  2
                </div>
                <div>
                  <h3 className="heading-5 mb-2">Intelligence Layer</h3>
                  <p className="body-medium text-on-dark">
                    <strong>LinkIQ</strong> adds marketing intelligence, SEO visibility, analytics, and smart link optimisation across all platforms.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-gradient-to-br from-[#00d9ff] via-[#0066ff] to-[#f43cde] text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                  3
                </div>
                <div>
                  <h3 className="heading-5 mb-2">Experience Layer</h3>
                  <p className="body-medium text-on-dark">
                    <strong>Voyae</strong> demonstrates how social-first commerce and community-driven experiences can be built on top of our platform infrastructure.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-gradient-to-br from-[#00d9ff] via-[#0066ff] to-[#f43cde] text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                  4
                </div>
                <div>
                  <h3 className="heading-5 mb-2">Delivery Layer</h3>
                  <p className="body-medium text-on-dark">
                    <strong>HighReach Apps</strong> delivers smart websites and mobile-ready apps for local and small businesses, including our fully white-labelled GoHighLevel (GHL) solution, turning the platform into real-world, revenue-generating tools.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Build For - White Background */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-5 md:px-10">
          <h2 className="heading-2 text-center mb-16">
            WHO WE BUILD OUR PRODUCTS FOR
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {audiences.map((audience, index) =>
            <div key={audience.title} className="bg-gray-50 border border-gray-200 rounded-xl p-6 hover:border-[#f43cde] transition-all hover:shadow-[0_0_30px_rgba(244,60,222,0.2)]">
                <div className="text-[#f43cde] mb-4">
                  {audience.icon}
                </div>
                <h3 className="heading-5 text-on-white mb-3">{audience.title}</h3>
                <p className="body-small text-muted-white">{audience.description}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Trust Section - Dark Background */}
      <section className="py-20 md:py-32 bg-black">
        <div className="container mx-auto px-5 md:px-10 text-center">
          <h2 className="heading-2 mb-8">
            POWERING MODERN DIGITAL OPERATIONS
          </h2>
          <p className="body-large max-w-2xl mb-12 mx-auto text-on-dark text-muted-dark">
            We integrate with industry-leading platforms and technologies to deliver seamless, scalable, and secure digital experiences.
          </p>
          
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
            <div className="caption text-[#F43CDE]">CRM & Customer Platforms</div>
            <div className="caption text-[#F43CDE]">AI & Intelligence Engines</div>
            <div className="caption text-[#F43CDE]">Automation & Workflow Systems</div>
            <div className="caption text-[#F43CDE]">Secure & Modern APIs</div>
          </div>
        </div>
      </section>

      {/* CTA Section - White Background */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-5 md:px-10 text-center">
          <h2 className="heading-2 mb-6">
            READY TO EXPLORE?
          </h2>
          <p className="body-large text-muted-white mb-8 max-w-2xl mx-auto">
            Discover how our products can transform your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products" className="btn-primary">
              View All Products
            </Link>
            <Link to="/contact" className="btn-secondary">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>);

};